import React from 'react';
import { useQuery } from 'react-query';
import { ContactBlock, ContactBlockHeaderProps } from '@jbhi-fi/components/src/ContactBlock';
import { ShopifySupportProvider } from '../../../../contexts/ShopifySupportContext';
import { useSupport } from '@jbhi-fi/components/src/context/SupportProvider';
import { styles } from './SupportBlock.css';
import { useIdPrefix } from '@jbhi-fi/components/src/IdPrefix';
import { getSalesLineHours } from '@jbhi-fi/components/src/ContactBlock/utils/salesLine/salesLine';
import { LegacyContactBlock } from '@jbhi-fi/components/src/ContactBlock/LegacyContactBlock/LegacyContactBlock';
import { usePublicApiClient } from '@jbhi-fi/components/src/hooks/usePublicApiClient';
import { ContactBlockTransformer } from '@jbhi-fi/components/src/Contentful/transformers/ContactBlockTransformer';
import { PublicApiClientProvider } from '@jbhi-fi/components/src/context/PublicApiClientProvider';
import { useContentfulCurrentWebsiteElements } from '@jbhi-fi/components/src/hooks/useContentfulCurrentWebsiteElements/useContentfulCurrentWebsiteElements';
import { ContentfulContactBlock } from '@jbhi-fi/apis/src/contentful/types';
import { ContentfulApiClient, ContentfulPreviewThemeApiClient } from '../../../../api/contentful/ContentfulClient';

export const SupportBlock = ({
  shouldShowSalesLineNumber,
  supportContactProps,
  isInstallationOffer = false,
}: {
  shouldShowSalesLineNumber: boolean;
  supportContactProps?: ContactBlockHeaderProps;
  isInstallationOffer?: boolean;
}) => {
  const suffixId = isInstallationOffer ? 'installation-cta' : 'seenitcheaper-cta';

  return (
    <ShopifySupportProvider>
      <div id={useIdPrefix(suffixId)} className={styles.container}>
        <PublicApiClientProvider
          contentfulThemeApiClient={new ContentfulPreviewThemeApiClient()}
          contentfulClient={new ContentfulApiClient()}
        >
          <SupportContactBlock
            shouldShowSalesLineNumber={shouldShowSalesLineNumber}
            supportContactProps={supportContactProps}
            isInstallationOffer={isInstallationOffer}
          />
        </PublicApiClientProvider>
      </div>
    </ShopifySupportProvider>
  );
};

const SupportContactBlock = ({
  shouldShowSalesLineNumber,
  supportContactProps,
  isInstallationOffer = false,
}: {
  shouldShowSalesLineNumber: boolean;
  supportContactProps?: ContactBlockHeaderProps;
  isInstallationOffer?: boolean;
}) => {
  const { phoneNumber, onCallClick, onChatClick } = useSupport();
  const { contentfulClient, contentfulThemeApiClient } = usePublicApiClient();
  const { currentWebsiteEntryIds } = useContentfulCurrentWebsiteElements();

  const isPreview = window.Jbhifi?.isContentPreview || false;
  const shouldUseThemeApi = isPreview || !contentfulClient;
  const client = shouldUseThemeApi ? contentfulThemeApiClient : contentfulClient;

  const { data: currentActiveContactBlock } = useQuery({
    enabled: !!currentWebsiteEntryIds?.pdpAskForADeal,
    queryKey: ['supportContactBlock', currentWebsiteEntryIds?.pdpAskForADeal],
    refetchOnWindowFocus: false,
    queryFn: () =>
      client?.getEntries<ContentfulContactBlock>({
        content_type: 'contactBlock',
        'sys.id': currentWebsiteEntryIds?.pdpAskForADeal ?? '',
      }),
  });
  const pdpAskForADealData = currentActiveContactBlock?.data?.items?.[0];
  const contactBlockProps = pdpAskForADealData
    ? ContactBlockTransformer.toContactBlockProps(pdpAskForADealData)
    : undefined;
  const { heading, subText, disclaimerText, chatText, chatUrl, callText, callUrl, contentPalette } =
    contactBlockProps || {};
  supportContactProps = supportContactProps
    ? supportContactProps
    : {
        heading,
        subText,
        disclaimerText,
        chatText,
        chatUrl,
        callText,
        callUrl,
        contentPalette,
        variant: 'narrow' as const,
      };
  return window.Jbhifi.storeLocation === 'NZ' && !isInstallationOffer ? (
    <LegacyContactBlock
      {...supportContactProps}
      chatText={chatText || 'Live chat'}
      callText={shouldShowSalesLineNumber ? supportContactProps.callText || `Call ${phoneNumber}` : ''}
      chatOnClick={onChatClick}
      callOnClick={onCallClick}
      salesLineHours={getSalesLineHours()?.replace('-', ' - ') || ''}
      variant="narrow"
    />
  ) : (
    <ContactBlock
      {...supportContactProps}
      chatText={chatText || 'Live chat'}
      callText={shouldShowSalesLineNumber ? supportContactProps.callText || `Call ${phoneNumber}` : ''}
      chatOnClick={onChatClick}
      shouldHideCallUs={!shouldShowSalesLineNumber}
      callOnClick={onCallClick}
      idComponent={isInstallationOffer ? 'installation' : undefined}
      salesLineHours={getSalesLineHours()}
      shouldShowExclusionText={!isInstallationOffer}
    />
  );
};
